import { AreaVariableType } from '@protector/entities/region/constants';
import type { AreaVariable } from '@protector/entities/region/models';
import type { Threshold } from '@protector/utils/constants';
import type { Dictionary, UUID } from '@protector/utils/types';
import convert from 'convert-units';
import type { ISTMarkerProps } from 'core/shared/map/map.models';
import { UnitSystem, formatNumber, getCurrentUnitSystem } from 'core/utils/functions';
import { mapboxgl } from 'core/utils/map/mapbox-gl';
import type { SegmentTrackingHookReturn } from 'core/utils/segment/useSegmentTracking';
import type { TFunction } from 'i18next';
import type { EventData, MapLayerEventType, Popup } from 'mapbox-gl';
import moment from 'moment';
import type { AnalyticContext } from '../fixed-points/fixed-points.models';
import { useIndicatorValuesStore } from './area-day-info/components/indicator-values.zustand';
import { EventType } from './timeline.models';

export const onClusterClick = ({
  event,
  mapbox,
  mapMarkers,
  enableMultiplePhotos,
  isGalleryV2,
  productsAvailable,
  segmentTracking,
  fieldName = ''
}: {
  event: MapLayerEventType['click'] & EventData;
  mapbox: mapboxgl.Map | undefined;
  mapMarkers: ISTMarkerProps[];
  enableMultiplePhotos: boolean;
  isGalleryV2: boolean;
  productsAvailable: boolean;
  segmentTracking: SegmentTrackingHookReturn;
  fieldName?: string;
}) => {
  if (!mapbox) return;
  const features = mapbox.queryRenderedFeatures(event.point, { layers: [`clusters`] });
  if (!features.length || !features[0].properties) return;
  const { cluster_id: clusterId, point_count } = features[0].properties as {
    cluster_id: number;
    point_count: number;
  };
  const clusterSource = mapbox.getSource('points');
  // Get all points under a cluster
  if (clusterSource?.type === 'geojson') {
    clusterSource.getClusterLeaves(clusterId, point_count, 0, (err, clusterLeavesFeatures) => {
      if (err) throw err;
      const clusteredMarkerIds = new Set(clusterLeavesFeatures.map(feature => feature.properties?.id as string));
      const clusteredMarkers = mapMarkers.filter(mapMarker => clusteredMarkerIds.has(mapMarker.id));
      if (clusteredMarkers.length === 0) return;

      const popupCluster = new mapboxgl.Popup({
        closeButton: true,
        closeOnClick: true,
        offset: 24
      })
        .setLngLat(event.lngLat)
        .setHTML('</ div>')
        .on('open', data => {
          const objectData = data as { target: Popup };
          mapbox.fire('close.popups');
          mapbox.fire('load.popupCluster', {
            data: clusteredMarkers,
            popup: objectData.target,
            enableMultiplePhotos,
            isGalleryV2,
            productsAvailable,
            segmentTracking,
            fieldName
          });
          mapbox.flyTo({
            center: event.lngLat
          });
          const handleClosePopups = () => {
            popupCluster.remove();
            mapbox.off('close.popups', handleClosePopups);
          };
          mapbox.on('close.popups', handleClosePopups);
        })
        .addTo(mapbox);
    });
  }
};

export const instanceClusterSourceOnMap = (
  icons: (marker: ISTMarkerProps) => string | undefined,
  mapbox: mapboxgl.Map | undefined,
  mapMarkers: ISTMarkerProps[],
  pointHightLight?: string,
  activeSeverityPoint?: boolean,
  severityPoints?: Dictionary<Threshold>
) => {
  const { showIndicatorValues, indicatorValues, activeSeveritySelection, isRainGaugePanelOpen } = useIndicatorValuesStore.getState();

  let filteredMarkers = mapMarkers;
  if (activeSeverityPoint && severityPoints) {
    const idsPoints = Object.keys(severityPoints);
    const hasSelectedMonitoringPoint = mapMarkers.some(
      item => item.markerProps.type === EventType.MONITORING && idsPoints.includes(item.id)
    );
    if (activeSeveritySelection?.type === 'trap') {
      filteredMarkers = mapMarkers.filter(item => item.markerProps.type !== EventType.TRAP || idsPoints.includes(item.id));
    } else if (hasSelectedMonitoringPoint) {
      filteredMarkers = mapMarkers.filter(item => item.markerProps.type !== EventType.MONITORING || idsPoints.includes(item.id));
    }
  }
  const source = mapbox?.getSource('points');
  if (source?.type === 'geojson') {
    source.setData({
      type: 'FeatureCollection',
      features: filteredMarkers.map(marker => {
        const selectedIndicatorValue = showIndicatorValues ? indicatorValues[marker.id] : undefined;
        const rainGaugeValue = showIndicatorValues && isRainGaugePanelOpen ? marker.valueLabel : undefined;
        const indicatorValue = marker.markerProps.type === EventType.PLUVIOMETER ? rainGaugeValue : selectedIndicatorValue;

        return {
          type: 'Feature',
          geometry: {
            type: 'Point',
            coordinates: [marker.markerProps.position[0], marker.markerProps.position[1]]
          },
          properties: {
            id: marker.id,
            icon: icons(marker),
            opacity: pointHightLight && marker.id !== pointHightLight ? 0.3 : 1,
            ...marker.markerProps,
            ...(indicatorValue ? { indicator_value: indicatorValue.value, indicator_value_color: indicatorValue.color } : {})
          }
        };
      })
    });
  }
};

export const getThresholdsFromAnalyticContextAndIndicatorId = (analyticContext: AnalyticContext, indicatorId: UUID) => {
  const customIndicator = analyticContext?.custom_indicator_dtos?.find(ci => ci.indicator_id === indicatorId);
  return customIndicator?.diagnostics?.[0].thresholds ?? [];
};

export const formatMetricSystem = (value: number): string => {
  const unitSystem = getCurrentUnitSystem();
  if (unitSystem === UnitSystem.IMPERIAL) {
    return `${formatNumber(convert(value).from('ha').to('ac'))} ac`;
  }
  return `${formatNumber(value)} ha`;
};

export const formatAreaVariableValue = (item: AreaVariable, t: TFunction) => {
  switch (item.class_name) {
    case AreaVariableType.AREA:
      return formatNumber(item.value, 2, 3);
    case AreaVariableType.AREA_DATE:
      return t('units.date', { date: moment(item.value) });
    default:
      return item.value;
  }
};
